import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  ChangeDetectorRef,
  AfterViewInit,
  ViewChild,
  ElementRef
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { Observable, Subject, combineLatest } from 'rxjs';
import { ChapterModel } from 'src/app/story/models/chapter.model';
import { StoryChapterThumbnail } from 'src/app/story/models/story-chapter-thumbnail.model';
import { ViewService } from '../../view.service';
import { takeUntil, debounceTime, auditTime } from 'rxjs/operators';
import { createElementID } from 'src/app/shared/libraries/dom/dom';

@Component({
  selector: 'app-view-chapters',
  templateUrl: './view-chapters.component.html',
  styleUrls: ['./view-chapters.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None

})
export class ViewChaptersComponent implements OnInit, OnDestroy, AfterViewInit {
  // ---------- View Children ----------
  @ViewChild('chapterList')
  chapterList: ElementRef<HTMLDivElement>;


  // ---------- Observables ----------
  @Select(StoryChaptersState.getStoryChapters)
  chapters$: Observable<ChapterModel[]>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(StoryChaptersState.getSelectedChapterIndex)
  selectedChapterIndex$: Observable<number>;

  @Select(StoryChaptersState.getRegisteredThumbnails)
  thumbnails$: Observable<Map<string, StoryChapterThumbnail>>;

  @Select(StoryChaptersState.getChaptersViewed)
  chaptersViewed$: Observable<string[]>;


  // ---------- Inputs ----------
  @Input()
  defaultImage = '/assets/images/view-chapter-thumbnail-placeholder.png';

  @Input()
  autoscroll = true;

  // ---------- Class Variables ----------
  ngUnsubscribe = new Subject();
  nextChapter: ChapterModel = null;



  // ---------- Lifecycle ----------
  constructor(
    private store: Store,
    private viewService: ViewService,
    private changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.initChaptersObserver();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  ngAfterViewInit() {
    this.initScrollObserver();
  }


  // ---------- Observers ----------
  initChaptersObserver() {
    combineLatest([
      this.store.select(StoryChaptersState.getStoryChapters),
      this.store.select(StoryChaptersState.getSelectedChapterIndex)
    ]).pipe(
      auditTime(300),
      takeUntil(this.ngUnsubscribe)
    ).subscribe(([chapters, index]) => {
      const nextIndex = index + 1;
      this.nextChapter = (nextIndex >= chapters.length) ? null : chapters[nextIndex];
      this.changeDetectorRef.markForCheck();
    });
  }

  initScrollObserver() {
    this.selectedChapterId$.pipe(
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapterId) => {
      this.scrollChapterIntoView(chapterId);
    });
  }



  // ---------- Interaction ----------
  handleChapterClick(chapter: ChapterModel) {
    this.viewService.selectChapter(chapter);
  }


  
  // ---------- Utility ----------
  getChapterThumb(chapterId: string): string {
    const thumbnails = this.store.selectSnapshot(StoryChaptersState.getRegisteredThumbnails);
    const thumbnail = thumbnails.get(chapterId);

    // use snapshot if present, otherwise use small
    if (!!thumbnail) {
      return thumbnail.snapshot || thumbnail.urls.small;
    } else {
      return '';
    }
  }

  scrollChapterIntoView(chapterId: string) {
    const elementId = createElementID(chapterId);
    if (!elementId) {
      return;
    }

    const target = <HTMLDivElement>this.chapterList.nativeElement.querySelector(`[data-chapter-id=${elementId}]`);

    if (!target || !this.autoscroll) {
      return;
    }

    // Manual check to see if the browser support smooth scrolling.
    // Although Safari supports scrollIntoView, it only has limited support via the boolean operator.
    // More importantly, the scrolled column jumps position when the mouse hovers over it.
    // For this reason we're not support Safari until Apple gets their act together.
    const isSmoothScrollSupported = 'scrollBehavior' in document.documentElement.style;
    if (isSmoothScrollSupported) {
      target.scrollIntoView({
        behavior: 'auto',
        block: 'nearest'
      });
    }
  }
    

  isViewed(chapterId: string) {
    return this.store.selectSnapshot(StoryChaptersState.getChaptersViewed).some(value => value === chapterId);
  }
    
}
